// ---------------------------------------------------------------------------
// GlobalTimeBar -- Leaf Component
// ---------------------------------------------------------------------------
// Renders a horizontal bar of time-period pills with an XTD dropdown
// and a Custom date range popover.
// ---------------------------------------------------------------------------

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { TIME_PERIODS, XTD_OPTIONS } from './constants'
import { CustomPeriodPopover } from './CustomPeriodPopover'
import type { TimePeriod, XtdOption } from './types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GlobalTimeBarProps {
  readonly selectedPeriod: TimePeriod
  readonly onPeriodChange: (period: TimePeriod) => void
  readonly customDateFrom?: string | null
  readonly customDateTo?: string | null
  readonly onCustomDatesChange?: (dateFrom: string, dateTo: string) => void
  readonly onCustomDatesClear?: () => void
  readonly hideCustom?: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const XTD_IDS: ReadonlySet<string> = new Set(
  XTD_OPTIONS.map((o) => o.id)
)

function isXtdActive(period: TimePeriod): boolean {
  return XTD_IDS.has(period)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function GlobalTimeBar({
  selectedPeriod,
  onPeriodChange,
  customDateFrom = null,
  customDateTo = null,
  onCustomDatesChange,
  onCustomDatesClear,
  hideCustom = false,
}: GlobalTimeBarProps) {
  const [isXtdOpen, setIsXtdOpen] = useState(false)
  const xtdRef = useRef<HTMLDivElement>(null)

  // Close XTD dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (xtdRef.current && !xtdRef.current.contains(event.target as Node)) {
        setIsXtdOpen(false)
      }
    }

    if (isXtdOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isXtdOpen])

  const handlePillClick = useCallback(
    (periodId: TimePeriod) => {
      if (periodId !== selectedPeriod) {
        onPeriodChange(periodId)
      }
    },
    [selectedPeriod, onPeriodChange]
  )

  const handleXtdToggle = useCallback(() => {
    setIsXtdOpen((prev) => !prev)
  }, [])

  const handleXtdOptionClick = useCallback(
    (optionId: XtdOption) => {
      onPeriodChange(optionId)
      setIsXtdOpen(false)
    },
    [onPeriodChange]
  )

  const handleCustomApply = useCallback(
    (dateFrom: string, dateTo: string) => {
      onCustomDatesChange?.(dateFrom, dateTo)
    },
    [onCustomDatesChange]
  )

  const handleCustomClear = useCallback(() => {
    onCustomDatesClear?.()
  }, [onCustomDatesClear])

  const xtdIsActive = isXtdActive(selectedPeriod)

  // Find the active XTD label if applicable
  const activeXtdLabel = xtdIsActive
    ? XTD_OPTIONS.find((o) => o.id === selectedPeriod)?.shortLabel
    : null

  return (
    <div
      data-testid="global-time-bar"
      className="flex items-center gap-3"
    >
      <span className="uppercase text-xs text-v11-text-muted font-medium whitespace-nowrap">
        Period:
      </span>

      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Standard time period pills */}
        {TIME_PERIODS.map((period) => {
          // Render Custom pill as popover trigger (skip if hideCustom)
          if (period.id === 'custom') {
            if (hideCustom) return null
            return (
              <CustomPeriodPopover
                key="custom"
                isActive={selectedPeriod === 'custom'}
                dateFrom={customDateFrom}
                dateTo={customDateTo}
                onApply={handleCustomApply}
                onClear={handleCustomClear}
              />
            )
          }

          const isActive = period.id === selectedPeriod

          return (
            <button
              key={period.id}
              data-testid={`time-pill-${period.id}`}
              type="button"
              aria-pressed={isActive}
              onClick={() => handlePillClick(period.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors border',
                isActive
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-v11-surface border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover'
              )}
            >
              {period.shortLabel}
            </button>
          )
        })}

        {/* XTD pill with dropdown */}
        <div ref={xtdRef} className="relative">
          <button
            data-testid="time-pill-xtd"
            type="button"
            aria-pressed={xtdIsActive}
            onClick={handleXtdToggle}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium transition-colors border flex items-center gap-1',
              xtdIsActive
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-v11-surface border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover'
            )}
          >
            {activeXtdLabel ?? 'XTD'}
            <svg
              className={cn('h-3 w-3 transition-transform', isXtdOpen && 'rotate-180')}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isXtdOpen && (
            <div
              data-testid="xtd-dropdown"
              className="absolute top-full left-0 mt-1 z-50 bg-v11-surface border border-v11-border rounded-lg shadow-lg py-1 min-w-[140px]"
            >
              {XTD_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  data-testid={`xtd-option-${option.id}`}
                  type="button"
                  onClick={() => handleXtdOptionClick(option.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs hover:bg-v11-surface-hover transition-colors',
                    selectedPeriod === option.id
                      ? 'text-blue-400 font-medium'
                      : 'text-v11-text-secondary'
                  )}
                >
                  {option.label} ({option.shortLabel})
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { GlobalTimeBar }
export type { GlobalTimeBarProps }
